import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useModalConfirm } from "@ryde/redux";
import {
  errors,
  IStationTimingPairStatsDto,
  Log,
  SimpleResult,
  simpleResultErrorInfo,
} from "@ryde/shared";

/**
 * Settles the stats request the measured-times dialog was opened on top of. The dialog opens before the
 * answer exists (RYDE-8064): the request runs for seconds on a long station list, and a header button
 * that only greys itself out for that long reads as a dead click, so the wait happens inside the dialog.
 *
 * A request that fails or comes back empty reports itself **inside** the dialog rather than closing it.
 * Toasting and self-closing was the pre-RYDE-8064 behaviour, and it made sense while the dialog only
 * ever appeared with data in it. Once it opens on a loader, a window that shows a spinner for a few
 * seconds and then disappears on its own reads as a glitch instead of as "there is nothing to show".
 *
 * Apply is released only once the numbers are on screen: the dialog opens with it disabled, so neither
 * the button nor the Enter shortcut can confirm a selection that cannot exist yet, and an outcome with
 * no numbers leaves it disabled for good.
 */
export const useRideMeasuredTimesStats = (
  statsPromise: Promise<SimpleResult<IStationTimingPairStatsDto>>,
) => {
  const { t } = useTranslation();
  const { updateModalOptions } = useModalConfirm();
  const [stats, setStats] = useState<IStationTimingPairStatsDto>();
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    let isActive = true;

    const resolveStats = async (): Promise<void> => {
      const statsResult = await statsPromise;

      if (!isActive) {
        return;
      }

      // Logged, not toasted: the dialog the user is looking at says the same thing, and a toast on top
      // of it would report the one failure twice.
      if (!statsResult.success) {
        const failureMessage = simpleResultErrorInfo(statsResult, t);
        Log.warn(failureMessage, statsResult.error);
        setErrorMessage(failureMessage);
        return;
      }

      if (!statsResult.data) {
        const emptyMessage = t(errors.stationTiming.statsNotLoaded);
        Log.warn(emptyMessage);
        setErrorMessage(emptyMessage);
        return;
      }

      setStats(statsResult.data);
      updateModalOptions({ isOkButtonDisabled: false });
    };

    void resolveStats();

    return () => {
      isActive = false;
    };
  }, [statsPromise, t, updateModalOptions]);

  return { errorMessage, stats };
};
